/**
 * The logic behind the leads filter panel, kept apart from the JSX so it can
 * be tested without rendering anything.
 *
 * Three things live here:
 *
 * - the draft and applied filter states, and how Search, Reset and switching
 *   tabs move between them;
 * - the date range, which is one control whose preset and custom range are
 *   never both set;
 * - the option lists the pickers show, grouped the way the owner reads them.
 */
import type { LeadFilters } from '../api/queries';
import { vocab } from '../vocab';

// ---------------------------------------------------------------------------
// Draft and applied filters
// ---------------------------------------------------------------------------

/**
 * Filters no longer apply as they are edited. The panel edits a draft, and
 * Search copies the draft onto the table.
 *
 * Both halves are overlays on the active tab's saved filters: `applied` null
 * means "what the tab saved", `draft` null means "the same as applied". That
 * way switching tabs is just clearing both, and the panel always opens on
 * exactly what the table is showing.
 */
export interface DraftState {
  applied: LeadFilters | null;
  draft: LeadFilters | null;
}

export const EMPTY_DRAFT: DraftState = { applied: null, draft: null };

export type DraftAction =
  /** Edit the draft. The table does not change. */
  | { type: 'edit'; patch: LeadFilters }
  /** Apply the draft to the table. */
  | { type: 'search' }
  /** Clear both the draft and the applied filters. */
  | { type: 'reset' }
  /** Drop every unsaved change: switching or deleting a tab, or discarding. */
  | { type: 'revert' }
  /**
   * The applied filters were just saved to the tab, so the tab now holds
   * them. Edits still waiting for Search stay in the panel.
   */
  | { type: 'saved' };

/** The filters the table is showing. */
export function appliedFilters(state: DraftState, saved: LeadFilters): LeadFilters {
  return state.applied ?? saved;
}

/** The filters the panel is showing. */
export function draftFilters(state: DraftState, saved: LeadFilters): LeadFilters {
  return state.draft ?? appliedFilters(state, saved);
}

export function reduceDraft(state: DraftState, action: DraftAction, saved: LeadFilters): DraftState {
  switch (action.type) {
    case 'edit':
      return { ...state, draft: { ...draftFilters(state, saved), ...action.patch } };
    case 'search':
      return { applied: cleanFilters(draftFilters(state, saved)), draft: null };
    case 'reset':
      return { applied: {}, draft: null };
    case 'revert':
      return EMPTY_DRAFT;
    case 'saved':
      return { applied: null, draft: hasPendingChanges(state, saved) ? state.draft : null };
  }
}

/** True when the panel holds edits that Search has not applied yet. */
export function hasPendingChanges(state: DraftState, saved: LeadFilters): boolean {
  return state.draft !== null && !filtersEqual(state.draft, appliedFilters(state, saved));
}

/** True when the table shows something other than what the tab saved. */
export function differsFromSaved(state: DraftState, saved: LeadFilters): boolean {
  return state.applied !== null && !filtersEqual(state.applied, saved);
}

/**
 * True when the panel or the table holds anything the tab has not saved.
 *
 * This, not differsFromSaved, decides whether Save to tab is offered. Picking
 * a new time frame and saving it straight away is a normal thing to do, and
 * making someone press Search first just to reveal the save button hid it.
 */
export function hasUnsavedChanges(state: DraftState, saved: LeadFilters): boolean {
  return !filtersEqual(draftFilters(state, saved), saved);
}

const PAGING = new Set(['page', 'page_size']);

/**
 * Filters with the empty values removed: null, '', [], false, and custom-field
 * rows with no field chosen (the server skips those anyway, so sending them
 * only makes two identical searches look different).
 */
export function cleanFilters(f: LeadFilters): LeadFilters {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(f)) {
    if (PAGING.has(k)) continue;
    if (k === 'field_filters' && Array.isArray(v)) {
      const rows = (v as NonNullable<LeadFilters['field_filters']>).filter(
        (r) => r.field.trim() !== '',
      );
      if (rows.length) out[k] = rows;
      continue;
    }
    if (v == null || v === '' || v === false) continue;
    if (Array.isArray(v) && v.length === 0) continue;
    out[k] = v;
  }
  return out as LeadFilters;
}

function canonical(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canonical);
  if (v && typeof v === 'object') {
    return Object.fromEntries(
      Object.keys(v as object)
        .sort()
        .map((k) => [k, canonical((v as Record<string, unknown>)[k])]),
    );
  }
  return v;
}

/** Equal as searches: same filters, ignoring empty values and key order. */
export function filtersEqual(a: LeadFilters, b: LeadFilters): boolean {
  return JSON.stringify(canonical(cleanFilters(a))) === JSON.stringify(canonical(cleanFilters(b)));
}

/** How many filters are set, ignoring paging. */
export function activeFilterCount(f: LeadFilters): number {
  return Object.keys(cleanFilters(f)).length;
}

// ---------------------------------------------------------------------------
// Date range
// ---------------------------------------------------------------------------

/*
  One date filter, not three.

  There used to be a preset dropdown on the toolbar, the same dropdown again
  inside More, and a separate From and To beside it, with nothing saying which
  won. The server answers that question in a way nobody could have guessed:
  `date_from.or(resolved_from)`, so a typed date quietly beats the preset, one
  end at a time.

  So the range is one control, and a custom range is one of its options. The
  two are mutually exclusive by construction: choosing a preset clears the
  custom range, and choosing or editing a custom range clears the preset.
*/
export const CUSTOM = 'custom';

export const DATE_PRESETS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

export const DAY_START = '00:00:00';
export const DAY_END = '23:59:59';

/** Today's date in the browser, as `YYYY-MM-DD`. */
export function todayIso(now: Date = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/**
 * The filter patch for choosing an option in the date control.
 *
 * Choosing Custom starts from the whole of today (00:00:00 to 23:59:59), so
 * the range is real the moment it is chosen, unless a custom range is already
 * set, in which case it is kept.
 */
export function dateRangePatch(
  value: string | null,
  current: LeadFilters = {},
  today: string = todayIso(),
): LeadFilters {
  if (value === CUSTOM) {
    if (current.date_from || current.date_to) return { date_range: null };
    return {
      date_range: null,
      date_from: `${today}T${DAY_START}`,
      date_to: `${today}T${DAY_END}`,
    };
  }
  return { date_range: value, date_from: null, date_to: null };
}

/** Which option the date control shows. A typed range always means Custom. */
export function dateSelectValue(filters: LeadFilters): string | null {
  if (filters.date_from || filters.date_to) return CUSTOM;
  return filters.date_range ?? null;
}

/** Normalise a time input's value to `HH:MM:SS`. */
function fullTime(t: string, fallback: string): string {
  if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) return t;
  return fallback;
}

/**
 * Split a stored bound into what the date and time inputs show. A bare date,
 * as saved by older tabs, means the start or end of that day.
 */
export function splitDateTime(v: string | null | undefined, end: boolean): { date: string; time: string } {
  const fallback = end ? DAY_END : DAY_START;
  if (!v) return { date: '', time: fallback };
  const [date = '', time = ''] = v.split('T');
  return { date, time: fullTime(time, fallback) };
}

/**
 * The patch for editing one end of a custom range. The server reads a
 * `T`-separated value without an offset as UK local time.
 */
export function customDatePatch(
  which: 'from' | 'to',
  date: string,
  time: string,
): LeadFilters {
  const end = which === 'to';
  const value = date ? `${date}T${fullTime(time, end ? DAY_END : DAY_START)}` : null;
  return which === 'from'
    ? { date_range: null, date_from: value }
    : { date_range: null, date_to: value };
}

function ukDateTime(v: string, end: boolean): string {
  const { date, time } = splitDateTime(v, end);
  const [y, m, d] = date.split('-');
  return `${d}/${m}/${y} ${time}`;
}

/** What the date control says when closed, e.g. `11/10/2026 00:00:00 - 11/10/2026 23:59:59`. */
export function dateRangeLabel(filters: LeadFilters): string {
  const { date_from: from, date_to: to } = filters;
  if (from && to) return `${ukDateTime(from, false)} - ${ukDateTime(to, true)}`;
  if (from) return `From ${ukDateTime(from, false)}`;
  if (to) return `Up to ${ukDateTime(to, true)}`;
  const preset = DATE_PRESETS.find((p) => p.value === filters.date_range);
  return preset?.label ?? 'Any time';
}

// ---------------------------------------------------------------------------
// Picker options
// ---------------------------------------------------------------------------

export interface Option {
  value: string;
  label: string;
}
export interface OptionGroup {
  group: string;
  items: Option[];
}

/** Active first, then inactive, leaving out a group with nothing in it. */
export function groupByActive<T extends { active: boolean }>(
  rows: T[],
  toOption: (r: T) => Option,
  labels: [string, string],
): OptionGroup[] {
  const byName = (a: Option, b: Option) => a.label.localeCompare(b.label);
  const groups: OptionGroup[] = [
    { group: labels[0], items: rows.filter((r) => r.active).map(toOption).sort(byName) },
    { group: labels[1], items: rows.filter((r) => !r.active).map(toOption).sort(byName) },
  ];
  return groups.filter((g) => g.items.length > 0);
}

/**
 * Campaigns grouped Active, Inactive and Reference lists. Reference lists hold
 * match values rather than leads, so they get a group of their own instead of
 * sitting among the campaigns (the server shows their rows only when one is
 * picked by name).
 */
export function campaignOptions(
  campaigns: { id: string; name: string; active: boolean; is_reference_list: boolean }[],
): OptionGroup[] {
  const v = vocab();
  const opt = (c: { id: string; name: string }) => ({ value: c.id, label: c.name });
  const groups = groupByActive(
    campaigns.filter((c) => !c.is_reference_list),
    opt,
    [`Active ${v.many('campaign')}`, `Inactive ${v.many('campaign')}`],
  );
  const refs = campaigns
    .filter((c) => c.is_reference_list)
    .map(opt)
    .sort((a, b) => a.label.localeCompare(b.label));
  return refs.length ? [...groups, { group: 'Reference lists', items: refs }] : groups;
}

/** The `supplier_ids` token for leads with no supplier. Matches the server. */
export const NO_SUPPLIER = '__none__';

export function supplierOptions(
  suppliers: { id: string; name: string; active: boolean }[],
): OptionGroup[] {
  const v = vocab();
  return [
    ...groupByActive(suppliers, (s) => ({ value: s.id, label: s.name }), [
      `Active ${v.many('supplier')}`,
      `Inactive ${v.many('supplier')}`,
    ]),
    { group: 'Other', items: [{ value: NO_SUPPLIER, label: `No ${v.one('supplier')}` }] },
  ];
}

// ---------------------------------------------------------------------------
// Rule builder
// ---------------------------------------------------------------------------

/** Operators that compare against nothing, so the value box is not needed. */
export const VALUELESS_OPERATORS = new Set(['is_empty', 'is_not_empty', 'isempty']);

/** What the rule builder needs from one entry of `GET /api/v1/lead-fields`. */
export interface SeenField {
  key: string;
  leads_with: number;
  built_in: boolean;
  computed: boolean;
  /** When the campaigns' all-time record last saw the key, if it has. */
  last_seen?: string | null;
}

/**
 * The fields a rule can be built on.
 *
 * `fields` is what `GET /api/v1/lead-fields` read from leads of the selected
 * campaigns (every campaign when none is selected), so a rule can name any
 * key leads carry, whatever the campaign selection. Until it has loaded, the
 * common fields stand in.
 *
 *  - Common fields: the standard lead fields (columns an import maps onto),
 *    then the built-in keys every routed lead carries. Computed keys (score,
 *    sold) are left out: they have their own filters above, and a lead's
 *    score is not in its custom data.
 *  - Seen on leads: every other key recent leads carry, commonest first.
 *  - Seen before: on none of the recent leads, but in the campaigns'
 *    all-time record of keys leads have carried, newest first.
 *
 * A key on no lead at all is not offered, but can be typed, and a saved
 * rule's field always shows as typed.
 *
 * Each option's label is the field's name in words (a definition's label, or
 * a built-in or standard field's name), or else the key itself; the panel
 * shows the key under the label when they differ. Nothing in the list says
 * where a label came from.
 */
export function ruleFieldOptions({
  standard,
  fields,
  labels = [],
  builtInLabel = (key) => key,
}: {
  standard: Option[];
  /** Fields seen on leads; undefined while loading. */
  fields: SeenField[] | undefined;
  /** Labels for keys that have one: the key as the value. */
  labels?: Option[];
  /** A built-in key in words, such as Source for `source`. */
  builtInLabel?: (key: string) => string;
}): OptionGroup[] {
  const v = vocab();
  const labelOf = new Map(labels.filter((o) => o.value && o.label).map((o) => [o.value, o.label]));
  const known = new Set<string>();
  // "Source" for `source` or "Supplier id" for `supplier_id` says nothing
  // the key does not, so the key stands alone.
  const plain = (x: string) => x.toLowerCase().replace(/[_\s]+/g, ' ').trim();
  const item = (key: string, label?: string): Option => {
    known.add(key);
    const words = labelOf.get(key) ?? label ?? key;
    return { value: key, label: plain(words) === plain(key) ? key : words };
  };
  const fresh = (key: string) => !!key && !known.has(key);

  const common = standard.filter((o) => fresh(o.value)).map((o) => item(o.value, o.label));
  const groups: OptionGroup[] = [{ group: 'Common fields', items: common }];
  if (fields) {
    const usable = fields.filter((f) => !f.computed);
    common.push(...usable.filter((f) => f.built_in && fresh(f.key)).map((f) => item(f.key, builtInLabel(f.key))));
    const seen = usable.filter((f) => !f.built_in && f.leads_with > 0 && fresh(f.key)).map((f) => item(f.key));
    if (seen.length) groups.push({ group: `Seen on ${v.many('lead')}`, items: seen });
    const before = usable
      .filter((f) => !f.built_in && f.leads_with === 0 && f.last_seen && fresh(f.key))
      .map((f) => item(f.key));
    if (before.length) groups.push({ group: 'Seen before', items: before });
  }
  return groups;
}

/** Whether an option matches what was typed, by its label or its key. */
export function fieldMatches(option: Option, typed: string): boolean {
  const q = typed.trim().toLowerCase();
  return !q || option.value.toLowerCase().includes(q) || option.label.toLowerCase().includes(q);
}
